/*
 * SiteFooter — shared footer rendered by AppLayout on every public page.
 * Nav links come from the backend (/api/navigation?location=footer) with a
 * hard-coded fallback; the tagline is admin-editable via home content.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, Drum } from 'lucide-react';
import { api } from '@/lib/api-client';
import { FALLBACK_FOOTER_NAV } from '@/lib/home-fallback-data';
import { useHomeContent } from '@/hooks/use-home-content';
import type { NavigationMenuItem } from '@shared/types';

const LEGAL_LABELS = ['Privacy Policy', 'Terms', 'Cookie Policy', 'Disclaimer', 'Data Deletion'];

export function SiteFooter() {
  const content = useHomeContent();
  const { data: navFooterData } = useQuery({
    queryKey: ['nav-footer'],
    queryFn: () => api<{ items: NavigationMenuItem[] }>('/api/navigation?location=footer'),
    staleTime: 10 * 60 * 1000,
  });

  const items = (navFooterData?.items ?? FALLBACK_FOOTER_NAV)
    .filter((item) => item.path !== '/admin' && item.path !== '/dashboard');
  const exploreItems = items.filter((i) => !LEGAL_LABELS.includes(i.label));
  const legalItems = items.filter((i) => LEGAL_LABELS.includes(i.label));

  return (
    /* pb-24 keeps the last row clear of the fixed mobile bottom nav. */
    <footer className="bg-emerald-950 text-gray-300 pt-14 pb-24 lg:pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white">
                <Drum className="h-5 w-5" />
              </div>
              <h3 className="text-white text-lg font-bold">Pooram<span className="text-primary">App</span></h3>
            </div>
            <p className="mb-4 text-sm leading-relaxed">
              The ultimate guide to Kerala&apos;s temple festivals — real-time updates, live streams, and a community of devotees and travellers.
            </p>
          </div>
          <nav aria-label="Explore">
            <h4 className="text-white text-lg font-bold mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              {exploreItems.map((item) => (
                <li key={item.id}><Link to={item.path} className="hover:text-white">{item.label}</Link></li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Support">
            <h4 className="text-white text-lg font-bold mb-4">Support</h4>
            <ul className="space-y-2 text-sm">
              {legalItems.map((item) => (
                <li key={item.id}><Link to={item.path} className="hover:text-white">{item.label}</Link></li>
              ))}
            </ul>
          </nav>
          <div>
            <h4 className="text-white text-lg font-bold mb-4">Stay updated</h4>
            <p className="text-sm mb-3">Get real-time festival alerts and reminders.</p>
            <Link to="/contribute" className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
              <Bell className="h-4 w-4" /> Notify me
            </Link>
          </div>
        </div>
        <div className="pt-6 border-t border-emerald-900/70 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} Pooram App. All rights reserved.</p>
          <p className="text-xs text-gray-500">{content.footerTagline}</p>
        </div>
      </div>
    </footer>
  );
}
